import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { BehaviorSubject, combineLatest, EMPTY, forkJoin, Observable, Observer, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';

import { ActionItem, BaseContainer, EllipsisMultiActionItem, RequestPaginationInfo } from '@stylehaven/components';
import { SavedQuerySearch } from '../../entities/saved-queries/saved-query-search.entity';
import { SavedQuery } from '../../entities/saved-queries/saved-query.entity';
import { PaginatedResults } from '../../record/entities/pagination/paginated-results.entity';
import { defaultPaginationInfo, PaginationInfo } from '../../record/entities/pagination/pagination-info.entity';
import { SavedQueriesOverviewSandboxService } from '../sandbox/saved-queries-sandbox.service';

@Component({
    standalone: false,
    selector: 'digihaven-saved-query-overview-container',
    templateUrl: './saved-query-overview-container.component.html'
})
export class SavedQueryOverviewContainerComponent extends BaseContainer implements OnInit, OnDestroy {
    private refresh$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    readonly pagination$: BehaviorSubject<PaginationInfo> = new BehaviorSubject(defaultPaginationInfo);

    readonly COLUMN_COUNT: number = 2;

    savedQueries$!: Observable<PaginatedResults<SavedQuery>>;
    actionItems$!: Observable<ActionItem[]>;

    constructor(
        private readonly sandbox: SavedQueriesOverviewSandboxService,
        private route: ActivatedRoute
    ) {
        super();
    }

    ngOnInit(): void {
        this.setupSourceStreams();
        this.buildActionItemStream();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.refresh$.complete();
    }

    updatePagination(pagination: RequestPaginationInfo): void {
        this.pagination$.next({ ...this.pagination$.value, ...pagination });
    }

    private setupSourceStreams(): void {
        const search$: Observable<SavedQuerySearch> = this.pagination$.pipe(
            map(pagination => this.mapPaginationToSearchParams(pagination))
        );

        this.savedQueries$ = combineLatest([search$, this.refresh$]).pipe(
            tap(() => this.startLoading()),
            switchMap(([search, _]) => this.sandbox.getSavedQueries(search).pipe(catchError(() => this.showError()))),
            tap(() => this.stopLoading()),
            shareReplay(1)
        );
    }

    private mapPaginationToSearchParams(pagination: PaginationInfo): SavedQuerySearch {
        return {
            nrOfResults: pagination.resultsPerPage,
            startIndex: pagination.page * pagination.resultsPerPage
        };
    }

    private buildActionItemStream(): void {
        this.actionItems$ = this.savedQueries$.pipe(
            takeUntil(this.destroy$),
            switchMap(queries => {
                const actionItems = queries.results.map(query => this.getActionItems(query));
                return forkJoin(actionItems);
            })
        );
    }

    private getActionItems(query: SavedQuery): Observable<ActionItem> {
        const tag = this.route.snapshot.data?.allObjectsTag || '';
        return of([
            this.sandbox.getDeleteSavedQueryAction(this.loading$),
            this.sandbox.getRestoreSavedQueryAction(this.loading$, tag)
        ]).pipe(
            switchMap(actionItems =>
                forkJoin(actionItems.map(actionItem => actionItem.toActionItem(query, this.observer)))
            ),
            map(actions => {
                const actionItems: ActionItem[] = [];
                actions.forEach(actionItem => {
                    if (actionItem) {
                        actionItems.push(actionItem);
                    }
                });

                return EllipsisMultiActionItem(actionItems);
            })
        );
    }

    get observer(): Observer<void> {
        return {
            next: () => this.refresh$.next(true),
            complete: () => {},
            error: () => {}
        };
    }

    private startLoading(): void {
        this.error$.next(false);
        this.loading$.startLoading();
    }

    private stopLoading(): void {
        this.loading$.stopLoading();
    }

    private showError(): Observable<never> {
        this.loading$.stopLoading();
        this.error$.next(true);
        return EMPTY;
    }

    trackById(index: number, query: SavedQuery): string {
        return query.Id;
    }
}
